import {
  AlignItems,
  Body2,
  Box,
  Color,
  Display,
  FlexDirection,
  FontFamily,
  Heading2,
  JustifyContent,
  Overflow,
  Text,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { CSSObject } from '@emotion/react'
import React from 'react'

import { useIsDesktopBreakpoint } from '../../hooks/useIsDesktopBreakpoint'
import { LandingPageContentSections, TrackingActions, trackCTAClick } from '../../tracking'
import { CWCLinkButton } from '../CWCLinkButton/CWCLinkButton'
import { Carousel } from '../EndlessVarietySection/Carousel'
import { CWCCollectionCard } from './components/CWCCollectionCard/CWCCollectionCard'
import { WEEKLY_COLLECTIONS } from './config'

const propertiesForTracking = {
  carouselSection: LandingPageContentSections.WEEKLY_COLLECTIONS,
}

// Responsive arrays are [base, 640, 768, 1024] (the CWCHero convention).
const SectionContainer = withCitrusTheme(Box, (theme) => [
  theme.responsiveStyle(['56px', '56px', '56px', '80px'], ['paddingTop']) as CSSObject,
  theme.responsiveStyle(['56px', '56px', '56px', '80px'], ['paddingBottom']) as CSSObject,
])

// Gutters live on each child, not the row, so the carousel column can bleed to the page edge (the
// CwC-carousel idiom); `minWidth: 0` stops it widening the row past the viewport. Right-bleed only:
// the row is full-width so the carousel reaches the viewport's right edge, while the desktop left
// inset grows above 1920px (`max(128px, (100vw − 1920)/2 + 128)`) so the text keeps its centred-1920
// left margin.
const SectionRow = withCitrusTheme(Box, (theme) => [
  { width: '100%', minWidth: 0 } as CSSObject,
  theme.responsiveStyle(
    ['0px', '0px', '0px', 'max(128px, calc((100vw - 1920px) / 2 + 128px))'],
    ['paddingLeft'],
  ) as CSSObject,
  theme.responsiveStyle(['24px', '24px', '24px', '128px'], ['gap']) as CSSObject,
])

// Two tones stacked inside one <h2>: each line is a block `Text` so it reads as a single heading;
// size (13 = 36/40) and family are passed per line. The red is Citrus Primary_600; the near-black
// ink is off-palette (nearest token ColdGrey_900 is #212930), so that one stays a raw hex.
const HeadingLineInk = withCitrusTheme(Text, () => ({ color: '#1A2026', display: 'block' }))
const HeadingLineRed = withCitrusTheme(Text, (theme) => ({
  color: theme.color(Color.Primary_600),
  display: 'block',
}))

// Left padding only aligns the first card with the text gutter (plus the Carousel's own 8px
// slidePaddingH); no right padding lets the carousel bleed to the page edge on the stacked layout.
const CarouselColumn = withCitrusTheme(Box, (theme) => [
  theme.responsiveStyle(['8px', '8px', '24px', '0px'], ['paddingLeft']) as CSSObject,
])

// Lifts the 24px row gap to the design's 32px carousel→CTA spacing (stacked layout only). `textAlign`
// centres the CTA below the carousel — from tablet up the button is a fixed 345px inline element.
const StackedCta = withCitrusTheme(Box, (theme) => [
  { textAlign: 'center' } as CSSObject,
  theme.responsiveStyle(['8px', '8px', '8px', '0px'], ['marginTop']) as CSSObject,
])

export type CWCWeeklyCollectionsSectionProps = {
  /** Destination for the CTA (the menu). */
  href: string
  /** Runs on CTA click after the anchor's native navigation is prevented. */
  onCTAClick: () => void
}

/**
 * CwC "Weekly collections for every moment" section — stacked below 1024, two columns (text |
 * carousel) at ≥1024. The CTA renders inside the text column on desktop and below the carousel when
 * stacked (the CWCSuppliersSection idiom).
 */
export function CWCWeeklyCollectionsSection({
  href,
  onCTAClick,
}: CWCWeeklyCollectionsSectionProps) {
  const isDesktop = useIsDesktopBreakpoint()

  const handleCTAClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    trackCTAClick(TrackingActions.THIS_WEEKS_MENU, LandingPageContentSections.WEEKLY_COLLECTIONS)
    onCTAClick()
  }

  const cta = (
    <CWCLinkButton href={href} variant="secondary" onClick={handleCTAClick}>
      Go to this week’s menu
    </CWCLinkButton>
  )

  return (
    <section data-testid="cwcWeeklyCollectionsSection">
      <SectionContainer
        bg={Color.White}
        display={Display.Flex}
        justifyContent={JustifyContent.Center}
      >
        <SectionRow
          display={Display.Flex}
          alignItems={[
            AlignItems.FlexStart,
            AlignItems.FlexStart,
            AlignItems.FlexStart,
            AlignItems.Center,
          ]}
          flexDirection={[
            FlexDirection.Column,
            FlexDirection.Column,
            FlexDirection.Column,
            FlexDirection.Row,
          ]}
        >
          <Box
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            flexShrink={0}
            minWidth="0"
            gap={6}
            paddingH={[4, 4, 8, 0]}
            width={['100%', null, null, '350px']}
          >
            {/* Capped here (not on the padded column) so the copy wraps to two lines at ~380px
              regardless of the column's gutters. */}
            <Box
              display={Display.Flex}
              flexDirection={FlexDirection.Column}
              gap={[3, 3, 3, 4]}
              maxWidth="380px"
            >
              <Heading2 size={13} fontFamily={FontFamily.ExtraBold}>
                <HeadingLineInk size={13} fontFamily={FontFamily.ExtraBold}>
                  Weekly collections
                </HeadingLineInk>
                <HeadingLineRed size={13} fontFamily={FontFamily.ExtraBold}>
                  for every moment
                </HeadingLineRed>
              </Heading2>
              <Body2 size={4} color={Color.ColdGrey_800}>
                More than mid-week dinners. Whatever the moment, there’s a perfect meal for it.
              </Body2>
            </Box>
            <Box display={[Display.None, Display.None, Display.None, Display.Block]} width="100%">
              {cta}
            </Box>
          </Box>

          <CarouselColumn
            flexGrow={1}
            minWidth="0"
            overflowX={Overflow.Hidden}
            width={['100%', null, null, 'auto']}
          >
            <Carousel
              carouselAreaLabel="weeklyCollectionsCarousel"
              options={{ align: 'start', loop: false }}
              showNavButtons={isDesktop}
              autoHideNavButtons
              slidePaddingH={2}
              snowplowTrackingProperties={propertiesForTracking}
            >
              {WEEKLY_COLLECTIONS.map((card) => (
                <Box key={card.caption}>
                  <CWCCollectionCard {...card} />
                </Box>
              ))}
            </Carousel>
          </CarouselColumn>

          <StackedCta
            display={[Display.Block, Display.Block, Display.Block, Display.None]}
            paddingH={[4, 4, 8, 0]}
            width="100%"
          >
            {cta}
          </StackedCta>
        </SectionRow>
      </SectionContainer>
    </section>
  )
}
